import type { GlMapCreateOperation } from '@festina/configuration-general-types/configuration'
import { dateUtils, type Except } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'

import { useMutateConvertExcelToGlMapCreateOperation } from '../../uiModels/api/glMaps/useMutateConvertExcelToGlMapCreateOperation'
import type { DraftCreationProps } from '../draftCreationModal/types'
import { ExcelUploadForm } from '../excelUploadForm/ExcelUploadForm'

type Props = Except<DraftCreationProps<'glMap'>, 'initialValue'> & {
  formId: string
  expectedMappingName?: string
}

/** Creates a GL map, or a new version of one, out of a GL-map Excel document. */
export const GlMapExcelUploadForm = ({
  formId,
  layer,
  block,
  expectedMappingName,
  onStatusChanged,
}: Props) => {
  const translate = useTranslate()
  const { mutateAsync } = useMutateConvertExcelToGlMapCreateOperation()

  return (
    <ExcelUploadForm
      formId={formId}
      layer={layer}
      block={block}
      expectedEntityName={expectedMappingName}
      excelConfig={{
        getEntityId: (operation: GlMapCreateOperation) => ({
          type: 'glMap',
          name: operation.glMap?.mappingId ?? '',
          fromDate: operation.glMap?.from
            ? dateUtils.dateUtcAsLocal(operation.glMap.from).toDate()
            : undefined,
          layer,
          block,
        }),
        convertToOperation: (excelFile) => mutateAsync({ excelFile }),
        missingEntityNameError: translate('glMapDraftForm.missingMappingName'),
        getEntityNameMismatchError: (mappingName, expectedMappingName) =>
          translate('glMapDraftForm.mappingNameMismatch', {
            mappingName,
            expectedMappingName,
          }),
        getUploadErrorMessage: (error) =>
          translate('glMapDraftForm.error', { error }),
      }}
      onStatusChanged={onStatusChanged}
    />
  )
}
